import { useState } from 'react';
import { Plus, Wrench } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { EmptyState } from '../../components/common/EmptyState';
import { MoneyInput } from '../../components/common/MoneyInput';
import { DatePicker } from '../../components/common/DatePicker';
import { formatMoney } from '../../lib/money';
import { MAINT_STATUS, MAINT_TYPES, TH, TD, LABEL, INPUT } from './config';
import type { Vehicle, VehicleMaintenance } from '../../types';

interface Props {
  records: (VehicleMaintenance & { vehicle_name?: string })[];
  loading: boolean;
  vehicles: Vehicle[];
  vehicleFilter: string | null;
  setVehicleFilter: (id: string | null) => void;
  onCreate: (input: Partial<VehicleMaintenance>, userId: string) => Promise<boolean>;
  onComplete: (id: string, cost: number | null, userId: string) => Promise<boolean>;
  userId: string;
}

export function MaintenanceTab({ records, loading, vehicles, vehicleFilter, setVehicleFilter, onCreate, onComplete, userId }: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const todayStr = new Date().toISOString().split('T')[0];

  // Auto-mark overdue for display
  const displayRecords = records.map(r => {
    if (r.status === 'scheduled' && r.scheduled_date && r.scheduled_date < todayStr) {
      return { ...r, _displayStatus: 'overdue' };
    }
    return { ...r, _displayStatus: r.status };
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={vehicleFilter ?? ''} onChange={e => setVehicleFilter(e.target.value || null)}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700, background: palette.white }}>
            <option value="">All Vehicles</option>
            {vehicles.filter(v => v.status !== 'retired').map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <span style={{ fontSize: 13, color: palette.g500 }}>{records.length} record{records.length !== 1 ? 's' : ''}</span>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 12.5, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={14} /> Schedule Maintenance
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: palette.g400, fontSize: 13 }}>Loading...</div>
      ) : records.length === 0 ? (
        <EmptyState icon={<Wrench size={22} />} title="No maintenance records" subtitle="Schedule maintenance to keep your fleet running." />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Vehicle</th><th style={TH}>Type</th><th style={TH}>Description</th>
            <th style={TH}>Scheduled</th><th style={TH}>Completed</th>
            <th style={{ ...TH, textAlign: 'right' }}>Cost</th><th style={TH}>Status</th><th style={TH}>Action</th>
          </tr></thead>
          <tbody>
            {displayRecords.map(r => {
              const ds = r._displayStatus;
              const s = MAINT_STATUS[ds] ?? MAINT_STATUS.scheduled;
              return (
                <tr key={r.id}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 500 }}>{r.vehicle_name ?? '—'}</td>
                  <td style={TD}><span style={{ textTransform: 'capitalize' }}>{r.type}</span></td>
                  <td style={TD}>{r.description}</td>
                  <td style={{ ...TD, color: ds === 'overdue' ? palette.red : palette.g700, fontWeight: ds === 'overdue' ? 600 : 400 }}>
                    {r.scheduled_date ?? '—'}
                  </td>
                  <td style={TD}>{r.completed_date ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{r.cost != null ? formatMoney(r.cost) : '—'}</td>
                  <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: s.bg, color: s.fg }}>{s.label}</span></td>
                  <td style={TD}>
                    {(ds === 'scheduled' || ds === 'overdue') && (
                      <button onClick={() => onComplete(r.id, null, userId)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', background: palette.greenWash, color: palette.green, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Complete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add maintenance form */}
      <AddMaintenancePanel isOpen={showAdd} onClose={() => setShowAdd(false)} vehicles={vehicles}
        onSave={async input => { const ok = await onCreate(input, userId); if (ok) setShowAdd(false); return ok; }} />
    </div>
  );
}

function AddMaintenancePanel({ isOpen, onClose, vehicles, onSave }: {
  isOpen: boolean; onClose: () => void; vehicles: Vehicle[];
  onSave: (input: Partial<VehicleMaintenance>) => Promise<boolean>;
}) {
  const [vehicleId, setVehicleId] = useState('');
  const [type, setType] = useState('scheduled');
  const [desc, setDesc] = useState('');
  const [date, setDate] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleClose = () => { setVehicleId(''); setType('scheduled'); setDesc(''); setDate(''); setCost(''); setNotes(''); onClose(); };

  const handleSave = async () => {
    setSaving(true);
    // Q1.5 boundary: hook accepts number; cost is canonical after MoneyInput blur.
    await onSave({ vehicle_id: vehicleId, type: type as VehicleMaintenance['type'], description: desc, scheduled_date: date || null, cost: cost ? Number(cost) : null, notes: notes || null, status: 'scheduled' });
    setSaving(false);
  };

  const F: React.CSSProperties = { marginBottom: 14 };
  const activeVehicles = vehicles.filter(v => v.status !== 'retired');

  return (
    <SlidePanel isOpen={isOpen} onClose={handleClose} title="Schedule Maintenance">
      <div style={F}><label style={LABEL}>Vehicle *</label><select style={INPUT} value={vehicleId} onChange={e => setVehicleId(e.target.value)}><option value="">Select...</option>{activeVehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
      <div style={F}><label style={LABEL}>Type</label><select style={INPUT} value={type} onChange={e => setType(e.target.value)}>{MAINT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
      <div style={F}><label style={LABEL}>Description *</label><input style={INPUT} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Oil change, tire rotation..." /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Scheduled date</label>
          <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Estimated cost</label><MoneyInput value={cost} onChange={setCost} /></div>
      </div>
      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={handleClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving || !vehicleId || !desc.trim()} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving || !vehicleId || !desc.trim() ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : 'Schedule'}</button>
      </div>
    </SlidePanel>
  );
}
